import { useCountAnimation } from "@/hooks/useCountAnimation";
import { Award, Heart, Users, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import beachBackground from "@/assets/ocean-view-background.jpg";

interface Differentiator {
  value?: number;
  prefix?: string;
  label: string;
  icon: React.ReactNode;
}

const DifferentiatorCard = ({ 
  value, 
  prefix = "", 
  label, 
  icon 
}: Differentiator) => {
  const { count, elementRef } = useCountAnimation({ 
    end: value || 0, 
    duration: 1200 
  });

  const formatNumber = (num: number) => {
    return num.toLocaleString("es-ES");
  };

  return (
    <div
      ref={elementRef}
      className="flex flex-col items-center text-center p-4 sm:p-6 rounded-lg bg-background/10 backdrop-blur-sm border border-primary/20 hover:bg-background/20 hover:border-primary/40 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
    >
      {icon}
      {value !== undefined && (
        <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' }}>
          {prefix}{formatNumber(count)}
        </div>
      )}
      <div className="text-xs sm:text-sm md:text-base text-white font-medium" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' }}>
        {label}
      </div>
    </div>
  );
};

const DifferentiatorSection = () => {
  const { t } = useTranslation();

  const differentiators: Differentiator[] = [
    {
      value: 1800,
      prefix: "+",
      label: t('differentiators.healthySmiles'),
      icon: <Heart className="w-8 h-8 mb-3 text-primary" />,
    },
    {
      value: 1700,
      prefix: "+",
      label: t('differentiators.dentalImplants'),
      icon: <Sparkles className="w-8 h-8 mb-3 text-primary" />,
    },
    {
      value: 1300,
      prefix: "+",
      label: t('differentiators.recoveredTeeth'),
      icon: <Users className="w-8 h-8 mb-3 text-primary" />,
    },
    {
      value: 15,
      prefix: "+",
      label: t('differentiators.yearsExperience'),
      icon: <Award className="w-8 h-8 mb-3 text-primary" />,
    },
  ];

  return (
    <section
      id="differentiators"
      className="relative py-16 sm:py-20 md:py-32 overflow-hidden"
      role="region"
      aria-label="Our achievements and expertise in dental care"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={beachBackground}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          width={1920}
          height={1080}
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/20 to-background/40"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center text-white mb-10 sm:mb-12 md:mb-16 px-2" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' }}>
          {t('differentiators.title')}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
          {differentiators.map((diff, index) => (
            <DifferentiatorCard key={index} {...diff} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DifferentiatorSection;
